import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  Autocomplete,
  TextField,
  MenuItem,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { css } from '@emotion/react'
import { useTheme, type Theme } from '@mui/material/styles'
import { BiSearch } from 'react-icons/bi'

import {
  useConnectorAutocomplete,
  type AutocompleteResult,
} from '@hooks/useConnectorAutocomplete'

// =============================================================================
// Styles
// =============================================================================

const useStyles = (theme: Theme) => ({
  option: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  `,
  optionPrimary: css`
    font-weight: 500;
  `,
  optionSecondary: css`
    font-size: 12px;
    color: ${theme.palette.text.secondary};
  `,
  label: css`
    display: block;
    margin-bottom: 2px;
    margin-left: 6px;
    font-size: 12px;
    font-weight: 600;
  `,
  labelRequired: css`
    color: ${theme.palette.error.main};
  `,
  inputWrapper: css`
    /* Prevent browser autocomplete dropdown from appearing */
    input::-webkit-contacts-auto-fill-button,
    input::-webkit-credentials-auto-fill-button {
      visibility: hidden;
      display: none !important;
      pointer-events: none;
      position: absolute;
      right: 0;
    }
    /* Hide browser autocomplete suggestions */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px white inset !important;
      box-shadow: 0 0 0 1000px white inset !important;
    }
  `,
});

// =============================================================================
// Types
// =============================================================================

export interface ConnectorAutocompleteProps {
  /** The trigger type (e.g., 'consumer-name') */
  triggerType: string;
  /** Input label */
  label?: string;
  /** Input name attribute */
  name?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Current input value (controlled) */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when a result is selected from dropdown */
  onSelect?: (result: AutocompleteResult) => void;
  /** Callback when input loses focus */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field has an error */
  error?: boolean;
  /** Helper text to display below input */
  helperText?: string;
  /** Custom start adornment (replaces default search icon) */
  startAdornment?: React.ReactNode;
  /** Custom end adornment (renders alongside loading spinner) */
  endAdornment?: React.ReactNode;
  /** Input type attribute (default: 'text') */
  inputType?: string;
  /** Max length for input */
  maxLength?: number;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Connector Autocomplete Component
 *
 * A text input with autocomplete suggestions from external APIs.
 * Falls back gracefully to a regular text input if integration is disabled.
 */
export function ConnectorAutocomplete({
  triggerType,
  label,
  name,
  placeholder,
  value,
  onChange,
  onSelect,
  onBlur,
  required,
  disabled,
  error,
  helperText,
  startAdornment,
  endAdornment,
  inputType,
  maxLength,
}: ConnectorAutocompleteProps) {
  const theme = useTheme();
  const styles = useStyles(theme);

  // Track if we're selecting to prevent blur handling
  const isSelectingRef = useRef(false);
  // Ref to directly access the input element for autocomplete prevention
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Track if we've just made a selection to prevent showing empty dropdown (use state so React re-renders)
  const [justSelected, setJustSelected] = useState(false);
  // Timer refs for cleanup on unmount
  const selectingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justSelectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timers on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (selectingTimerRef.current) {
        clearTimeout(selectingTimerRef.current);
      }
      if (justSelectedTimerRef.current) {
        clearTimeout(justSelectedTimerRef.current);
      }
    };
  }, []);

  // Use the autocomplete hook
  const {
    results,
    loading,
    enabled,
    search,
    clear,
    handleSelect: hookHandleSelect,
  } = useConnectorAutocomplete({
    triggerType,
    onSelect: result => {
      isSelectingRef.current = true;
      onSelect?.(result);
      // Reset flag after short delay (store ref for cleanup)
      if (selectingTimerRef.current) {
        clearTimeout(selectingTimerRef.current);
      }
      selectingTimerRef.current = setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    },
  });

  // Control when the dropdown should be open
  // CRITICAL: If justSelected is true, ALWAYS close the dropdown (highest priority)
  // Only show dropdown when:
  // 1. There are results to display, OR
  // 2. Loading is true (user is actively searching)
  // Do NOT show "No results found" - it causes the empty dropdown to persist after selection
  // Do NOT show when user just clicks into the field with no input
  // Do NOT show after a selection has been made (results cleared)
  const shouldShowDropdown = !justSelected && enabled && (results.length > 0 || loading);

  // Handle input change
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string, reason: string) => {
      onChange(newValue);
      // Reset selection flag when user types
      if (reason === 'input') {
        setJustSelected(false);
      }

      if (reason === 'input' && enabled) {
        search(newValue);
      } else if (reason === 'clear') {
        clear();
        setJustSelected(false);
      }
    },
    [onChange, enabled, search, clear]
  );

  // Handle selection from dropdown
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string | AutocompleteResult | null) => {
      if (newValue && typeof newValue === 'object') {
        // Selected from dropdown
        const displayValue = String(newValue.displayValue ?? newValue.id ?? '');
        // Immediately mark selection and clear results BEFORE updating value
        // This ensures the dropdown closes synchronously
        setJustSelected(true);
        clear();
        onChange(displayValue);
        hookHandleSelect(newValue);
        // Reset the flag after a delay to allow normal behavior to resume (store ref for cleanup)
        if (justSelectedTimerRef.current) {
          clearTimeout(justSelectedTimerRef.current);
        }
        justSelectedTimerRef.current = setTimeout(() => {
          setJustSelected(false);
        }, 300);
      } else if (typeof newValue === 'string') {
        // Typed value (freeSolo)
        onChange(newValue);
        // Reset selection flag when user types
        setJustSelected(false);
      } else if (newValue === null) {
        // Value was cleared
        onChange('');
        clear();
        setJustSelected(false);
      }
    },
    [onChange, hookHandleSelect, clear]
  );

  // Handle blur
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // Don't fire blur if we're in the middle of selecting
      if (isSelectingRef.current) {
        return;
      }

      // Clear results on blur
      clear();

      // Call parent blur handler
      onBlur?.(event);
    },
    [clear, onBlur]
  );

  // Render the label
  const renderLabel = () => {
    if (!label) return null;
    return (
      <label css={styles.label}>
        {label}
        {required && <span css={styles.labelRequired}> *</span>}
      </label>
    );
  };

  return (
    <div css={styles.inputWrapper}>
      {renderLabel()}
      <Autocomplete
        freeSolo
        fullWidth
        disabled={disabled}
        options={results}
        inputValue={value}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={loading}
        loadingText='Searching...'
        // Controlled open state - must use both open, onOpen, and onClose for full control
        open={shouldShowDropdown}
        onOpen={() => {
          // Prevent opening if we just made a selection - this is a no-op that
          // works with the controlled open prop to prevent MUI from overriding
        }}
        onClose={(_event, reason) => {
          // Clear results when closing to ensure clean state
          if (reason === 'selectOption' || reason === 'blur') {
            clear();
          }
          // Only reset justSelected when NOT selecting an option
          // When selecting, handleChange already set justSelected=true and we need to keep it
          // to prevent the empty dropdown from showing (since value.length >= 3 triggers "no results" state)
          if (reason !== 'selectOption') {
            setJustSelected(false);
          }
        }}
        // Blur the input after selection to ensure dropdown closes
        blurOnSelect
        // Explicitly ensure dropdown closes on selection (this is default, but being explicit)
        disableCloseOnSelect={false}
        noOptionsText={value.length >= 3 ? 'No results found' : 'Type to search...'}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          return String(option.displayValue ?? option.id ?? '');
        }}
        isOptionEqualToValue={(option, val) => {
          if (typeof option === 'string' || typeof val === 'string') return false;
          return option.id === val.id;
        }}
        filterOptions={x => x} // Don't filter - server handles filtering
        // Control the popper to hide it when empty or just selected
        slotProps={{
          popper: {
            // Hide the popper visually when there are no results, not loading, or just selected
            style: {
              display: (results.length === 0 && !loading) || justSelected ? 'none' : undefined,
            },
          },
          paper: {
            // Also hide the paper component when empty or just selected
            style: {
              display: (results.length === 0 && !loading) || justSelected ? 'none' : undefined,
            },
          },
        }}
        renderOption={(props, option) => (
          <MenuItem
            {...props}
            key={String(option.id)}
            onMouseDown={e => {
              // Prevent input blur when clicking option
              e.preventDefault();
            }}
          >
            <div css={styles.option}>
              <Typography variant='body2' css={styles.optionPrimary}>
                {String(option.displayValue ?? '')}
              </Typography>
              {option.subtext && (
                <Typography variant='caption' css={styles.optionSecondary}>
                  {String(option.subtext)}
                </Typography>
              )}
            </div>
          </MenuItem>
        )}
        renderInput={params => {
          // Use "new-password" as autocomplete value - Chrome respects this and won't show autocomplete
          // This is a common workaround since browsers ignore "off" for security/UX reasons
          const autocompleteValue = 'new-password';

          return (
            <TextField
              {...params}
              fullWidth
              type={inputType}
              name={name}
              required={required}
              error={error}
              placeholder={placeholder}
              helperText={helperText}
              onBlur={handleBlur}
              autoComplete={autocompleteValue}
              inputRef={ref => {
                // Store ref for direct manipulation
                inputRef.current = ref;
                // Directly set autocomplete attribute on the input element
                if (ref) {
                  ref.setAttribute('autocomplete', autocompleteValue);
                  ref.setAttribute('data-lpignore', 'true');
                  ref.setAttribute('data-form-type', 'other');
                  ref.setAttribute('data-1p-ignore', 'true');
                  ref.setAttribute('data-bwignore', 'true');
                }
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: autocompleteValue,
                maxLength: maxLength,
                // Additional attributes to prevent browser autocomplete
                'data-lpignore': 'true',
                'data-form-type': 'other',
                'data-1p-ignore': 'true', // 1Password ignore
                'data-bwignore': 'true', // Bitwarden ignore
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: startAdornment !== undefined ? (
                    <InputAdornment position='start' sx={{ ml: 1, mr: 0 }}>
                      {startAdornment}
                    </InputAdornment>
                  ) : (
                    <InputAdornment position='start' sx={{ ml: 1, mr: 0 }}>
                      <BiSearch size={20} color={theme.palette.action.active} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color='inherit' size={20} />}
                      {endAdornment}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          );
        }}
      />
    </div>
  );
}

export default ConnectorAutocomplete
